// Browser-local autosave for long-form article editors (blog + wiki).
//
// Redux form state is lost on a page reload / crash / accidental close. These
// editors can hold a lot of work, so we mirror the counselling-reply autosave
// (apps/forum/components/CounsellingQuestion.tsx): persist the in-progress
// title/body to localStorage as the user types, then offer to restore it when
// they come back. All access is guarded - losing the draft on an unavailable
// localStorage (private mode, quota, SSR) is acceptable degradation.
import { SmartTextareaEditorType } from "@/mk2/components/forms/SmartTextareaField";

export type ArticleDraftKind = "blog" | "wiki";

export interface ArticleDraft {
    title: string;
    // HTML / Markdown depending on editorType; maps to the "body" (blog) or
    // "message" (wiki) form field - the caller normalizes the field name.
    body: string;
    editorType: SmartTextareaEditorType;
    savedAt: number;
}

// One key per editing session: per article in edit mode, per user for a new
// article (there is no id yet). The kind and the edit/new segment are explicit
// separators so blog/wiki never clash and a string id can never collide with
// another mode's slot.
export function articleDraftKey(
    kind: ArticleDraftKind,
    isEdit: boolean,
    id: string | number,
): string {
    return `article_draft_${kind}_${isEdit ? "edit" : "new"}_${id}`;
}

export function saveArticleDraft(key: string, draft: ArticleDraft): void {
    if (typeof window === "undefined" || !key) {
        return;
    }

    try {
        window.localStorage.setItem(key, JSON.stringify(draft));
    } catch {
        // localStorage may be unavailable (private mode, quota, disabled).
    }
}

export function loadArticleDraft(key: string): ArticleDraft | null {
    if (typeof window === "undefined" || !key) {
        return null;
    }

    try {
        const raw = window.localStorage.getItem(key);
        if (!raw) {
            return null;
        }

        const parsed = JSON.parse(raw) as Partial<ArticleDraft>;
        if (
            !parsed ||
            typeof parsed.title !== "string" ||
            typeof parsed.body !== "string" ||
            typeof parsed.savedAt !== "number" ||
            (parsed.editorType !== SmartTextareaEditorType.RICHTEXT &&
                parsed.editorType !== SmartTextareaEditorType.MARKDOWN)
        ) {
            // Reject partial / stale-shape records so the editor is never
            // re-hydrated with an undefined editorType (would mis-init the
            // RICHTEXT/MARKDOWN editor).
            return null;
        }

        return parsed as ArticleDraft;
    } catch {
        return null;
    }
}

export function clearArticleDraft(key: string): void {
    if (typeof window === "undefined" || !key) {
        return;
    }

    try {
        window.localStorage.removeItem(key);
    } catch {
        // ignore
    }
}

// The restore banner is only worth showing when the saved draft actually
// differs from what the form already holds (the server version in edit mode,
// or the placeholder in create mode) - otherwise there is nothing to restore.
export function draftDiffersFrom(draft: ArticleDraft, title: string, body: string): boolean {
    return draft.title !== title || draft.body !== body;
}
